import { useQueries } from "@tanstack/react-query";

import { appsKeys, getApp, useConnections } from "@/shared/api/apps";

import type { Agent, ToolRisk } from "@/shared/types";

/** One thing a person can be asked to approve: a tool by name, or an app action by its slug. */
export interface ApprovalOption {
  name: string;
  label: string;
  desc: string;
  risk?: ToolRisk;
  logo?: string | null;
}

/** The apps tool: listing it asks before every app action, whichever it is. */
const EVERY_APP_ACTION: ApprovalOption = {
  name: "remote_tool_exec",
  label: "Every app action",
  desc: "Any action of a linked app, whichever it is.",
};

/* The sandbox tools as the backend names them (`backend/agent/tools/sandbox`).
   Ceiling: no route lists an agent's tools, so this list is kept by hand. */
export const SANDBOX_TOOLS = [
  "bash", "bash_output", "bash_stop", "read_file", "write_file", "edit_file", "multi_edit",
  "delete_file", "glob", "grep", "preview_url", "share_file",
];

/* The web tools (`backend/agent/tools/web.py`), kept by hand for the same reason. */
export const WEB_TOOLS = [
  { name: "web_search", desc: "Ten results for a query, and the top pages' text when it needs them" },
  { name: "web_fetch", desc: "Up to ten pages by link, read at once" },
];

/**
 * What the agent's Needs approval list may name, as publish checks it: every action
 * of the apps linked to it (from each app's catalog spec, the one `search_app_tools`
 * reads), and the tools its switches give it. `ask_user` is left out: it already
 * waits on a person.
 */
export default function useApprovalOptions(agent: Agent): { options: ApprovalOption[]; loading: boolean } {
  const caps = agent.capabilities;
  const { data: connections = [], isPending } = useConnections(agent.id);
  const slugs = caps.appsEnabled
    ? [...new Set(connections.filter((c) => c.link?.enabled !== false).map((c) => c.app.slug))]
    : [];
  const apps = useQueries({ queries: slugs.map((slug) => ({ queryKey: appsKeys.detail(slug), queryFn: () => getApp(slug) })) });

  const actions = apps.flatMap(({ data: app }) =>
    (app?.spec.tools ?? []).map((tool): ApprovalOption => ({
      name: tool.slug,
      label: tool.name,
      desc: `${app!.name} · ${tool.desc}`,
      risk: tool.risk,
      logo: app!.logo,
    })),
  );
  const sandbox = caps.sandboxEnabled
    ? SANDBOX_TOOLS.map((name): ApprovalOption => ({ name, label: name, desc: "A sandbox tool" }))
    : [];
  const web = caps.webEnabled ? WEB_TOOLS.map(({ name, desc }): ApprovalOption => ({ name, label: name, desc })) : [];
  return {
    options: [...(caps.appsEnabled ? [EVERY_APP_ACTION, ...actions] : []), ...sandbox, ...web],
    loading: (caps.appsEnabled && isPending) || apps.some((q) => q.isPending),
  };
}
